import { Component, ChangeDetectionStrategy, ChangeDetectorRef, Inject } from '@angular/core';
import {
    MatLegacyDialog as MatDialog,
    MatLegacyDialogRef as MatDialogRef,
    MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
} from '@angular/material/legacy-dialog';
import { MatLegacySelectChange as MatSelectChange } from '@angular/material/legacy-select';
import { IamOrganizationsApiService, Role } from '@kempoweroyj/ngx-iam-organizations-api-client';
import { UserTenancyAccessObject } from '@kempowersw/ngx-iam-users-api-client';
import { OrganizationSearchResultMinimized } from '@kempoweroyj/ngx-search-api-client';
import { sortBy as _sortBy, get as _get, set as _set } from 'lodash';
import { AuthUserRole, AuthSystemRole } from 'providers/auth.service';
import { BehaviorSubject, filter, from, Observable } from 'rxjs';
import { MultiselectAutocompleteItem } from 'shared/multiselect-autocomplete/multiselect-autocomplete.component';
import { OrganizationService } from 'shared/organization-info/organization.service';
import { AutocompleteOption } from 'shared/search-with-autocomplete/search-with-autocomplete.component';
import { getUserAccessDescriptionString, UserRoles } from 'shared/utils/parseSystemRole';
import { translateSystemRole } from 'shared/utils/translateSystemRole';

export interface ImpersonateDialogData {
    userTenancyAccesses: UserTenancyAccessObject[];
}

const TenantRoleMultiSelectCache: {
    [tenantId: string]: MultiselectAutocompleteItem;
} = {};

interface TenantToImpersonate {
    tenantId: string;
    userRole?: AuthUserRole;
    label: string;
    systemRoles?: AuthSystemRole[];
    roles?: string[];
}
interface SystemRoleToImpersonate {
    name: string;
    id: AuthSystemRole;
}
const AUTH_SYSTEM_ROLES: Readonly<AuthSystemRole>[] = [
    'systemAdmin',
    'orgAdmin',
    'default',
    'readonly',
    'maintenance',
    'chargerDiagnostics',
];

@Component({
    selector: 'impersonate-dialog',
    templateUrl: './impersonate-dialog.component.html',
    styleUrls: ['./impersonate-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ImpersonateDialogComponent {
    selectedTenant: TenantToImpersonate;
    autocompleteTenants$ = new BehaviorSubject<AutocompleteOption[]>([]);

    tenantRoles$: Observable<MultiselectAutocompleteItem[]>;
    selectedTenantRoles: string[] = [];

    loading = true;

    userTenancies: UserTenancyAccessObject[];
    selectedTenancyAccess: UserTenancyAccessObject;
    selectedTenancyAccessPrivileges: string;

    static open(matDialog: MatDialog, data?: ImpersonateDialogData) {
        return matDialog.open(ImpersonateDialogComponent, { data });
    }

    defaultRole = true;
    selectedSystemRole: AuthSystemRole = 'default';
    systemRolesToImpersonate: SystemRoleToImpersonate[] = AUTH_SYSTEM_ROLES.map((role) => {
        return {
            name: translateSystemRole(role),
            id: role,
        };
    });

    get isSysAdmin(): boolean {
        return !this.userTenancies?.length;
    }

    constructor(
        private dialogRef: MatDialogRef<ImpersonateDialogComponent>,
        private cdRef: ChangeDetectorRef,
        private orgService: OrganizationService,
        private iamOrganizationsApi: IamOrganizationsApiService,
        @Inject(MAT_DIALOG_DATA) public data: ImpersonateDialogData
    ) {
        if (data?.userTenancyAccesses) {
            this.userTenancies = data.userTenancyAccesses;
            this.cdRef.markForCheck();
        }
    }

    async ngOnInit() {
        this.orgService.organizations$.pipe(filter((orgs) => orgs !== null)).subscribe((response) => {
            this.autocompleteTenants$.next(this._mapTenants(response));
            this.loading = false;
            this.cdRef.markForCheck();
        });

        this.loading = false;
        this.cdRef.markForCheck();
    }

    private _mapTenants(response: OrganizationSearchResultMinimized[]): AutocompleteOption[] {
        const tenants: AutocompleteOption[] = [];
        for (const r of response) {
            const tenantId = r.organization.tenantId;
            const isEmsp = tenantId === 'TESTING';
            tenants.push({
                id: tenantId,
                label: r.organization.name || tenantId,
                value: {
                    tenantId,
                    // 'A' = 'cpo', 'B' = 'maintenance' default to 'cpo' if type not set for some reason
                    userRole: isEmsp ? 'emsp' : 'cpo',
                    label: r.organization.name || tenantId,
                    customerId: r.organization.customerId,
                } as TenantToImpersonate,
            });
        }

        return _sortBy(tenants, [(t) => t.label.toLowerCase()]);
    }

    private _getTenantRoles(tenantId: string): Observable<MultiselectAutocompleteItem[]> {
        return new Observable((observer) => {
            // PERFORMANCE: return cache first if available for instant response
            const cache = _get(TenantRoleMultiSelectCache, tenantId, undefined);
            if (cache !== undefined) {
                observer.next(cache);
            }

            from(this.iamOrganizationsApi.getRole_(tenantId)).subscribe((r) => {
                const fresh = r.response as Role[];

                // Map to multiselect
                const mapped: MultiselectAutocompleteItem[] = fresh.map((r) => {
                    return { name: r.name, value: r.roleId };
                });

                _set(TenantRoleMultiSelectCache, tenantId, mapped);

                // ...then, return the fresh data (component will update if needed)
                observer.next(mapped);
            });
        });
    }

    selectedTenantRolesChanged(update: MultiselectAutocompleteItem[]) {
        this.selectedTenantRoles = update.map((i) => i.value);
        this.cdRef.markForCheck();
    }

    selectTenant(option: AutocompleteOption) {
        this.selectedTenant = option.value;
        this.cdRef.markForCheck();

        // fetch tenant-created roles
        const tenantId = this.selectedTenant.tenantId;
        this.tenantRoles$ = this._getTenantRoles(tenantId);

        this.cdRef.markForCheck();
    }

    useCustomRole() {
        this.defaultRole = false;
        this.cdRef.markForCheck();
    }

    async changeSelectedUserTenancyAccess(event: MatSelectChange): Promise<void> {
        if (event?.value) {
            const selection: UserTenancyAccessObject = event.value;
            const sysRoles: AuthSystemRole[] = selection.systemRoles.map((role) => role as AuthSystemRole);

            this.selectedTenant = {
                tenantId: selection.targetTenantId,
                systemRoles: sysRoles,
                roles: selection.roles,
                label: selection.targetTenantName,
            };

            // Leave roles empty for now as we currently only have the custom-role IDs stored in the UserTenancyAccessResponse and don't want to display those in the UI.
            const roles: UserRoles = { roles: [], systemRoles: sysRoles };

            this.selectedTenancyAccessPrivileges = getUserAccessDescriptionString(roles);
            this.selectedTenancyAccess = selection;

            this.cdRef.markForCheck();
        }
    }

    ok() {
        this.dialogRef.close({
            tenantId: this.selectedTenant.tenantId,
            userRole: this.selectedTenant.userRole,
            systemRoles: this.userTenancies ? this.selectedTenant.systemRoles : [this.selectedSystemRole],
            roles: this.selectedTenantRoles,
            tenantName: this.selectedTenant.label,
        });
    }

    cancel() {
        this.dialogRef.close();
    }
}
